import { t } from '@/lib/i18n';
import { Link } from '@tanstack/react-router';
import { FolderClosed, Search } from 'lucide-react';
import type { Folder } from '@shared/types';
import { CenteredSpinner, EmptyState, ErrorState } from '@/components/ui';
import { useFolders, useLibraryStats } from '@/lib/queries';

/**
 * The landing screen. Deliberately shows folders rather than the whole library:
 * with ~1400 assets, "All Assets" was a wall of thumbnails that told nobody
 * anything. Assets appear once a folder is chosen (here or in the left rail).
 */
export function HomePage() {
  const { data, isPending, error, refetch } = useFolders();
  const { data: stats } = useLibraryStats();
  const roots = data?.tree ?? [];

  return (
    <div className="flex h-full flex-col">
      <header className="border-b line surface-card px-5 py-3">
        <h1 className="truncate text-base font-semibold text-strong">{t('homePage.library')}</h1>
        <p className="text-xs text-muted">
          {stats ? t('homePage.nAssets', { n: stats.total }) : t('homePage.entireLibrary')}
          {roots.length ? ` · ${roots.length === 1 ? t('homePage.oneFolder') : t('homePage.nFolders', { n: roots.length })}` : ''}
        </p>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-5">
        {isPending ? (
          <CenteredSpinner label={t('homePage.loadingFolders')} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : roots.length === 0 ? (
          <EmptyState
            icon={<FolderClosed />}
            title={t('homePage.noFoldersYet')}
            description={t('homePage.noFoldersYetDescription')}
          />
        ) : (
          <>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3">
              {roots.map((f) => (
                <FolderTile key={f.id} folder={f} />
              ))}
            </div>
            <Link
              to="/search"
              className="mt-6 inline-flex items-center gap-2 text-xs text-muted hover:text-strong"
            >
              <Search className="size-3.5" />
              {t('homePage.orSearchWholeLibrary')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/** Total assets in a subtree — the badge should reflect what's actually inside. */
function subtreeCount(f: Folder): number {
  return (f.assetCount ?? 0) + (f.children ?? []).reduce((n, c) => n + subtreeCount(c), 0);
}

function FolderTile({ folder }: { folder: Folder }) {
  const total = subtreeCount(folder);
  const subs = folder.children?.length ?? 0;
  return (
    <Link
      to="/folders/$folderId"
      params={{ folderId: folder.id }}
      className="group flex flex-col gap-3 rounded-[var(--radius-card)] border line surface-card p-4 transition-colors hover:border-brand-500"
      title={folder.path}
    >
      <FolderClosed className="size-7 text-brand-500" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-strong">{folder.name}</p>
        <p className="text-xs text-muted">
          {total === 1 ? t('homePage.oneAsset') : t('homePage.nAssets', { n: total })}
          {subs ? ` · ${subs === 1 ? t('homePage.oneSubfolder') : t('homePage.nSubfolders', { n: subs })}` : ''}
        </p>
      </div>
    </Link>
  );
}
